import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_CHECKBOX, KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_PDFEXPORT } from '@progress/kendo-angular-pdf-export';
import { KENDO_PDFVIEWER } from '@progress/kendo-angular-pdfviewer';
import { rotatePdfBase64 } from './pdf-rotate';

@Component({
  selector: 'gd-pdfviewer',
  standalone: true,
  imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        KENDO_CHECKBOX,
        KENDO_INPUTS,
        KENDO_BUTTON,
        KENDO_BUTTON,
        KENDO_PDFVIEWER,
        KENDO_PDFEXPORT,
        ],
  template: `
    <div class="rounded-xl shadow-md p-4">
      <div class="flex justify-end gap-2 pb-2">
        <button type="button" title="Rotate left" (click)="rotate(-90)" [disabled]="!pdfData || rotating"
          class="px-3 py-1 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
          <i class="fa fa-rotate-left"></i> Rotate left
        </button>
        <button type="button" title="Rotate right" (click)="rotate(90)" [disabled]="!pdfData || rotating"
          class="px-3 py-1 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
          <i class="fa fa-rotate-right"></i> Rotate right
        </button>
      </div>
      <kendo-pdfviewer
        style="height: 740px; width: 100%;"
        [data]="pdfData"
        [zoom]="zoom"
      >
      </kendo-pdfviewer>
    </div>
  `,
})
export class PDFViewerComponent implements OnChanges, AfterViewInit, OnDestroy {
  /** Accepts either Blob or string */
  @Input() rawPdfData?: Blob | string;

  /**
   * Initial zoom level. Kendo's own default is INITIAL_ZOOM_LEVEL = 1.25, which
   * opened every document at 125%; 1 shows it at 100%. Also accepts
   * 'fitToPage' / 'fitToWidth'.
   */
  @Input() zoom: number | 'fitToPage' | 'fitToWidth' = 1;

  /** Final data for PDFViewer */
  public pdfData?: Blob | string;

  public rotating = false;

  /** Rotates all pages and reloads them into the viewer (Kendo has no rotate tool). */
  async rotate(delta: 90 | -90): Promise<void> {
    if (typeof this.pdfData !== 'string' || this.rotating) return;
    this.rotating = true;
    try {
      this.pdfData = await rotatePdfBase64(this.pdfData, delta);
    } catch (err) {
      console.error('PDF rotate failed', err);
    } finally {
      this.rotating = false;
    }
  }

  ngOnChanges(): void {
    this.onInitOrChange();
  }

  ngAfterViewInit(): void {
    this.onInitOrChange();
  }

  private onInitOrChange(): void {
    if (this.rawPdfData instanceof Blob) {
      // Convert Blob to data URL
      const reader = new FileReader();
      reader.onload = () => {
        this.pdfData = reader.result as string; // full data URL
      };
      reader.readAsDataURL(this.rawPdfData);
    } else if (typeof this.rawPdfData === 'string') {
      // If string, ensure proper prefix
      this.pdfData = this.rawPdfData.startsWith('data:')
        ? this.rawPdfData
        : `data:application/pdf;base64,${this.rawPdfData}`;
    }
  }

  ngOnDestroy(): void {
    this.pdfData = undefined;
  }
}
